import { TimeMetric } from '../../../types';
import {
  IAbwesenheitStatusbarListModel,
  ITimeBalanceStatusbarModel,
  IVacationStatusbarModel,
  IWorkTimeStatusbarModel,
} from '../models/api-models';
import { DateTimeUtils } from './DateUtils';

export class MetricsHelper {
  public static calculatePendingTimesMetrics(
    timeBalanceStatusBar?: ITimeBalanceStatusbarModel,
  ): TimeMetric {
    var pendingTimes = timeBalanceStatusBar?.PendingTimes ?? 0;
    var totalTimes = timeBalanceStatusBar?.TotalTimes ?? 0;

    var pendingTimesPercentage = 0;
    if (pendingTimes != 0) {
      pendingTimesPercentage = (100 / totalTimes) * pendingTimes;
    }

    var pendingTimesLabel = `Offene Zeiten: ${DateTimeUtils.getFormattedDuration(pendingTimes / 60)}`;
    var remainingTimesLabel = `Restliche Zeiten: ${DateTimeUtils.getFormattedDuration((totalTimes - pendingTimes) / 60)}`;

    const pendingTimesMetrics: TimeMetric = {
      // title: 'Pendente Zeiten',
      // helpText:
      //   'Hier werden alle Zeiten angezeigt, die der Vorgesetzte noch nicht validiert hat, also weder genehmigt noch abgelehnt sind.',
      icon: 'icon-file-clock',
      colorLight: '#99AAB7',
      colorDark: '#52606B',
      values: [
        ...(pendingTimesPercentage > 0
          ? [
              {
                value: pendingTimesPercentage,
                color: '#E4F3FF',
                tooltip: pendingTimesLabel,
              },
            ]
          : []),
        ...(pendingTimesPercentage > 0 && pendingTimesPercentage < 100
          ? [
              {
                value: 100 - pendingTimesPercentage,
                color: '#E2E8ED',
                tooltip: remainingTimesLabel,
              },
            ]
          : []),
      ],
    };

    return pendingTimesMetrics;
  }

  public static calculateTimeBalanceMetrics(
    timeBalanceStatusBar?: ITimeBalanceStatusbarModel,
  ): TimeMetric {
    var totalTimes = timeBalanceStatusBar?.TotalTimes ?? 0;
    var provisionalTimes = timeBalanceStatusBar?.ProvisionalTimes ?? 0;

    var provisionalTimesPercentage = 0;
    if (provisionalTimes != 0) {
      provisionalTimesPercentage = (100 / totalTimes) * provisionalTimes;
    }

    var provisionalTimesLabel = `Offene Zeiten: ${DateTimeUtils.getFormattedDuration(provisionalTimes / 60)}`;
    var remainingTimesLabel = `Restliche Zeiten: ${DateTimeUtils.getFormattedDuration((totalTimes - provisionalTimes) / 60)}`;

    const timeBalanceMetrics: TimeMetric = {
      // title: 'Offene Zeiten',
      // helpText:
      //   'Hier werden alle Zeiten angezeigt, die noch nicht für die Validierung an den Vorgesetzten freigegeben wurden.',
      icon: 'icon-file-input',
      colorLight: '#99AAB7',
      colorDark: '#52606B',
      values: [
        ...(provisionalTimesPercentage > 0
          ? [
              {
                value: provisionalTimesPercentage,
                color: '#E4F3FF',
                tooltip: provisionalTimesLabel,
              },
            ]
          : []),
        ...(provisionalTimesPercentage > 0 && provisionalTimesPercentage < 100
          ? [
              {
                value: 100 - provisionalTimesPercentage,
                color: '#E2E8ED',
                tooltip: remainingTimesLabel,
              },
            ]
          : []),
      ],
    };

    return timeBalanceMetrics;
  }

  public static calculateVacationMetric(
    vacationStatusBar?: IVacationStatusbarModel,
  ): TimeMetric {
    var ferienDauer = vacationStatusBar?.VacationDaysTaken ?? 0;
    var feriensaldoDauer = vacationStatusBar?.VacationBalanceInDays ?? 0;
    var gesamteDauer = ferienDauer + feriensaldoDauer;
    var minutenProTag = vacationStatusBar?.WorkingDayInMinutes ?? 0;

    var ferienProz = (ferienDauer / gesamteDauer) * 100;
    var ferienDauerLabel = `Erfasste Ferien: ${DateTimeUtils.getFormattedDurationInDays(ferienDauer, minutenProTag)}`;
    var feriensaldoDauerLabel = `Verfügbare Ferien: ${DateTimeUtils.getFormattedDurationInDays(feriensaldoDauer, minutenProTag)}`;

    const values = [];

    if (ferienDauer > 0) {
      values.push({
        value: ferienProz,
        color: '#E4F3FF',
        tooltip: ferienDauerLabel,
      });
    }

    if (feriensaldoDauer > 0) {
      values.push({
        value: 100 - ferienProz,
        color: '#E1FCD5',
        tooltip: feriensaldoDauerLabel,
      });
    }

    const vacationMetrics = {
      title: 'Ferien',
      helpText: '',
      icon: 'icon-plane',
      colorLight: '#E1FCD5',
      colorDark: '#6AB849',
      values: values,
    };

    return vacationMetrics;
  }

  public static calculateWorkTimeMetric(
    worktimeStatusBar?: IWorkTimeStatusbarModel,
  ): TimeMetric {
    var workEntriesDuration = worktimeStatusBar?.WorkEntriesDuration ?? 0;
    var targetTimeDuration = worktimeStatusBar?.TargetTimeDuration ?? 0;
    var currentTargetTimeDuration =
      worktimeStatusBar?.CurrentTargetTimeDuration ?? 0;
    var flexTimeDuration = worktimeStatusBar?.FlexTime ?? 0;
    var remainingTimeDuration = targetTimeDuration - currentTargetTimeDuration;

    if (flexTimeDuration > 0)
      remainingTimeDuration = Math.max(
        remainingTimeDuration - flexTimeDuration,
        0,
      );

    var flexTimeIndicatorColor = flexTimeDuration >= 0 ? '#E1FCD5' : '#FA9898';
    flexTimeDuration = Math.abs(flexTimeDuration);

    // Labels:
    var workEntriesLabel = `Erfasste Zeiten: ${DateTimeUtils.getFormattedDuration(workEntriesDuration / 60)}`;
    var flexTimeLabel = `Gleitzeit: ${DateTimeUtils.getFormattedDuration(flexTimeDuration / 60)}`;
    var remainingTimeLabel = `Restzeit: ${DateTimeUtils.getFormattedDuration(remainingTimeDuration / 60)}`;

    // Normalize percentages:
    var sum = workEntriesDuration + flexTimeDuration + remainingTimeDuration;

    const values = [
      {
        value: (workEntriesDuration / sum) * 100,
        color: '#E4F3FF',
        tooltip: workEntriesLabel,
      },
      {
        value: (flexTimeDuration / sum) * 100,
        color: flexTimeIndicatorColor,
        tooltip: flexTimeLabel,
      },
      {
        value: (remainingTimeDuration / sum) * 100,
        color: '#E2E8ED',
        tooltip: remainingTimeLabel,
      },
    ].filter((x) => x.value);

    const timeBalanceMetrics: TimeMetric = {
      // title: 'Arbeitszeitsaldo',
      // helpText:
      //   'Hier werden alle geleisteten Zeiten angezeigt. Die Sollzeit berechnet sich aufgrund der Beschäftigung und der abgezogenen Abwesenheiten, Feiertage und Ferien. Die positive und negative Gleitzeit wird mittels einer roten oder grünen Statusbar dargestellt.',
      icon: 'icon-clock',
      colorLight: '#E4F3FF',
      colorDark: '#398AC8',
      values: values,
    };
    return timeBalanceMetrics;
  }

  public static calculateAbsenceMetrics(
    data?: IAbwesenheitStatusbarListModel,
  ): TimeMetric {
    var absencesDuration = 0;
    var workMinutesPerDay = 0;

    if (data?.AbwesenheitList?.length)
      workMinutesPerDay = data.AbwesenheitList[0].ArbeitstagInMinuten;

    var abwesenheitList = data?.AbwesenheitList;

    if (abwesenheitList != null) {
      for (var i = 0; i < abwesenheitList.length; i++) {
        absencesDuration += abwesenheitList[i].Dauer;
      }
    }

    var absenceDurationLabel = `Erfasste Abwesenheiten: ${DateTimeUtils.getFormattedDurationInDays(absencesDuration / workMinutesPerDay, workMinutesPerDay)}`;

    const values = absencesDuration
      ? [
          {
            value: 100,
            color: '#FFEBD4',
            tooltip: absenceDurationLabel,
          },
        ]
      : [];

    const timeBalanceMetrics: TimeMetric = {
      // title: 'Abwesenheiten',
      // helpText: '',
      icon: 'icon-ambulance',
      colorLight: '#FFEBD4',
      colorDark: '#F29323',
      values: values,
    };
    return timeBalanceMetrics;
  }
  // PBI #2223: Package-Verteilung fuer das Admin-Dashboard (Firmenuebersicht).
  public static calculatePackageDistributionMetric(
    basic: number,
    pro: number,
  ): TimeMetric {
    const total = basic + pro;
    const basicPercentage = total > 0 ? (basic / total) * 100 : 0;
    const proPercentage = total > 0 ? (pro / total) * 100 : 0;

    const values = [
      ...(basic > 0
        ? [
            {
              value: basicPercentage,
              color: '#E4F3FF',
              tooltip: `Basic: ${basic}`,
            },
          ]
        : []),
      ...(pro > 0
        ? [
            {
              value: proPercentage,
              color: '#FFEBD4',
              tooltip: `Pro: ${pro}`,
            },
          ]
        : []),
    ];

    return {
      icon: 'icon-package',
      colorLight: '#E4F3FF',
      colorDark: '#398AC8',
      values: values,
    };
  }

  // PBI #2223: Subscription-Status-Verteilung fuer das Admin-Dashboard.
  // "Other" und "OhneStatus" sind bewusst neutral eingefaerbt (Grau) - keine
  // Ampellogik, da beide keinen automatischen Handlungsbedarf bedeuten
  // (siehe IST-Doku 6.33).
  public static calculateSubscriptionDistributionMetric(
    trialing: number,
    active: number,
    other: number,
    ohneStatus: number,
  ): TimeMetric {
    const total = trialing + active + other + ohneStatus;
    const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

    const values = [
      ...(trialing > 0
        ? [
            {
              value: pct(trialing),
              color: '#E4F3FF',
              tooltip: `Trialing: ${trialing}`,
            },
          ]
        : []),
      ...(active > 0
        ? [
            {
              value: pct(active),
              color: '#E1FCD5',
              tooltip: `Active: ${active}`,
            },
          ]
        : []),
      ...(other > 0
        ? [
            {
              value: pct(other),
              color: '#99AAB7',
              tooltip: `Other: ${other}`,
            },
          ]
        : []),
      ...(ohneStatus > 0
        ? [
            {
              value: pct(ohneStatus),
              color: '#E2E8ED',
              tooltip: `Ohne Status: ${ohneStatus}`,
            },
          ]
        : []),
    ];

    return {
      icon: 'icon-credit-card',
      colorLight: '#E1FCD5',
      colorDark: '#6AB849',
      values: values,
    };
  }
}
